import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { setStoredCurrencyCode } from '../utils/currency';
import { setStoredLanguageCode } from '../utils/i18n';
import { useTheme } from './ThemeContext';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { changeAccentColor, resetAccentColor } = useTheme();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gymSubscription, setGymSubscription] = useState(() => {
    const stored = localStorage.getItem('flexigym_subscription');
    try {
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const storedToken = localStorage.getItem('flexigym_token');
    const storedUser = localStorage.getItem('flexigym_user');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        
        // Token expiration check disabled - tokens remain valid until logout
        // Validate token claims match stored user data
        const tokenClaims = getTokenClaims(storedToken);
        if (!validateTokenUserMatch(tokenClaims, parsedUser)) {
          console.warn('Token claims do not match stored user data, clearing session');
          localStorage.removeItem('flexigym_user');
          localStorage.removeItem('flexigym_token');
          localStorage.removeItem('flexigym_subscription');
          return;
        }
        
        setToken(storedToken);
        setUser(parsedUser);

        // Load gym settings for gym owners and members
        if (parsedUser.role === 'gym_owner' && parsedUser.gym_id) {
          loadGymSettings(storedToken, parsedUser.gym_id);
        } else if (parsedUser.role === 'member' && parsedUser.gym_id) {
          loadMemberGymSettings(storedToken, parsedUser.gym_id);
        }
      } catch (e) {
        console.error('Error parsing stored user data:', e);
        localStorage.removeItem('flexigym_user');
        localStorage.removeItem('flexigym_token');
        localStorage.removeItem('flexigym_subscription');
      }
    }
    setLoading(false);
  }, []);

  const loadGymSettings = async (authToken, gymId) => {
    try {
      const response = await api.get('/api/gym/profile', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.data.gym) {
        const gym = response.data.gym;
        if (gym.currency) {
          setStoredCurrencyCode(gym.currency, gymId);
        }
        if (gym.language) {
          setStoredLanguageCode(gym.language, gymId);
        }
        const colorToSet = gym.accent_color || '#f97316';
        changeAccentColor(colorToSet, gymId);
        if (gym.subscription) {
          setGymSubscription(gym.subscription);
          localStorage.setItem('flexigym_subscription', JSON.stringify(gym.subscription));
        }
      }
    } catch (error) {
      console.error('Failed to load gym settings:', error);
    }
  };

  const loadMemberGymSettings = async (authToken, gymId) => {
    try {
      const response = await api.get('/api/members/gym-status', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.data) {
        const data = response.data;
        if (data.currency) {
          setStoredCurrencyCode(data.currency, gymId);
        }
        if (data.language) {
          setStoredLanguageCode(data.language, gymId);
        }
        const colorToSet = data.accent_color || '#f97316';
        changeAccentColor(colorToSet, gymId);
        if (data.subscription) {
          setGymSubscription(data.subscription);
          localStorage.setItem('flexigym_subscription', JSON.stringify(data.subscription));
        }
      }
    } catch (error) {
      console.error('Failed to load member gym settings:', error);
    }
  };

  const login = async (newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('flexigym_token', newToken);
    localStorage.setItem('flexigym_user', JSON.stringify(newUser));

    if (newUser.role === 'gym_owner' && newUser.gym_id) {
      await loadGymSettings(newToken, newUser.gym_id);
    } else if (newUser.role === 'member' && newUser.gym_id) {
      await loadMemberGymSettings(newToken, newUser.gym_id);
    } else {
      // Super admin or other users default to Orange
      changeAccentColor('#f97316');
    }
  };

  const logout = async () => {
    const gymId = user?.gym_id;
    
    try {
      await api.post('/api/auth/logout');
    } catch (err) {
      console.error('Logout API call failed:', err);
    }
    
    setToken(null);
    setUser(null);
    setGymSubscription(null);
    localStorage.removeItem('flexigym_token');
    localStorage.removeItem('flexigym_user');
    localStorage.removeItem('flexigym_subscription');
    
    // Reset accent color to default Orange (#f97316)
    resetAccentColor();

    if (gymId) {
      localStorage.removeItem(`flexigym_currency_${gymId}`);
      localStorage.removeItem(`flexigym_language_${gymId}`);
    }
  };

  // Enhanced authentication checks
  const isAuthenticated = !!token && !!user;
  
  // Role-based checks
  const isSuperAdmin = user?.role === 'super_admin';
  const isGymOwner = user?.role === 'gym_owner';
  const isMember = user?.role === 'member';
  
  // Permission checks
  const hasPermission = (permission) => {
    if (!user) return false;
    
    const permissions = getRolePermissions(user.role);
    return permissions[permission] || false;
  };
  
  // Check if user can access admin features
  const canAccessAdminFeatures = isSuperAdmin;
  
  // Check if user can access gym management
  const canAccessGymManagement = isSuperAdmin || isGymOwner;
  
  // Get user's gym information
  const getUserGym = () => {
    if (!user) return null;
    
    return {
      id: user.gym_id,
      name: user.gym_name,
      address: user.gym_address,
      phone: user.gym_phone
    };
  };
  
  // Check if user belongs to a specific gym
  const belongsToGym = (gymId) => {
    if (isSuperAdmin) return true; // Super admins can access all gyms
    return user?.gym_id === gymId;
  };
  
  // Get user display information
  const getUserDisplayInfo = () => {
    if (!user) return null;
    
    return {
      name: user.name,
      email: user.email,
      role: user.role,
      roleDisplay: getRoleDisplayName(user.role),
      initials: getInitials(user.name),
      gym: getUserGym(),
      isSuperAdmin,
      isGymOwner,
      isMember
    };
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      gymSubscription,
      login, 
      logout, 
      isAuthenticated,
      // Role checks
      isSuperAdmin,
      isGymOwner,
      isMember,
      // Permission checks
      hasPermission,
      canAccessAdminFeatures,
      canAccessGymManagement,
      // Utility functions
      getUserGym,
      belongsToGym,
      getUserDisplayInfo
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper functions
const getTokenClaims = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

const validateTokenUserMatch = (tokenClaims, userData) => {
  if (!tokenClaims || !userData) return false;
  
  // Validate essential fields match between token and stored user data
  const roleMatches = tokenClaims.role === userData.role;
  
  // Skip gym_id validation for super admin
  if (tokenClaims.role === 'super_admin' || userData.role === 'super_admin') {
    return roleMatches;
  }
  
  // For other roles, compare gym_id with String coercion to avoid type mismatches
  const gymIdMatches = String(tokenClaims.gym_id) === String(userData.gym_id);
  
  return roleMatches && gymIdMatches;
};

const getRolePermissions = (role) => {
  const permissions = {
    'super_admin': {
      'canManageAllGyms': true,
      'canManageAllUsers': true,
      'canViewAllData': true,
      'canModifySubscriptions': true,
      'canViewAnalytics': true,
      'canManageSystemSettings': true,
      'canAccessAdminPanel': true
    },
    'gym_owner': {
      'canManageAllGyms': false,
      'canManageAllUsers': false,
      'canViewAllData': false,
      'canModifySubscriptions': false,
      'canViewAnalytics': true,
      'canManageSystemSettings': false,
      'canAccessAdminPanel': false,
      'canManageOwnGym': true,
      'canManageOwnMembers': true
    },
    'member': {
      'canManageAllGyms': false,
      'canManageAllUsers': false,
      'canViewAllData': false,
      'canModifySubscriptions': false,
      'canViewAnalytics': false,
      'canManageSystemSettings': false,
      'canAccessAdminPanel': false,
      'canManageOwnGym': false,
      'canManageOwnMembers': false,
      'canViewOwnData': true
    }
  };
  
  return permissions[role] || permissions['member'];
};

const getRoleDisplayName = (role) => {
  const roleNames = {
    'super_admin': 'Super Administrator',
    'gym_owner': 'Gym Owner',
    'member': 'Member'
  };
  
  return roleNames[role] || 'User';
};

const getInitials = (name) => {
  if (!name) return 'U';
  
  return name
    .split(' ')
    .map(part => part.charAt(0).toUpperCase())
    .join('')
    .substring(0, 2);
};
